import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER_COLUMN, H_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement, empty } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { asAnyObject } from "@cervval.js/common/src/tools/any.tools";
import { NoBubblesEventInit } from "@cervval.js/common/src/tools/event.tools";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import { anyReallyAny, getVOFValue, ValueOrFunction } from "@cervval.js/common/src/tools/object.tools";
import { default as Quill, Range } from "quill";
import { IImageDataMinifyOption, ImageData } from "quill-image-drop-and-paste";
import Clipboard from "quill/modules/clipboard";
import History from "quill/modules/history";
import Toolbar from "quill/modules/toolbar";
import { CjsEditor, EditorOnChangeEvent, EditorOptions } from "../editor";
import { CjsQuillLinkModule } from "./modules/link/quillLinkModule";
import { BaseRichTextConfigurationsKey, BaseRichTextToolbarConfigurationsValues, richTextBaseConfig, RichTextConfiguration } from "./richTextEditor.tools";

export interface RichTextEditorOptions extends EditorOptions {

    /**
     * Configuration of RichTextEditor 
     * @see https://quilljs.com/docs/configuration
     */
    configuration: RichTextConfiguration | BaseRichTextConfigurationsKey;

    /** Arialabel of Quill Editor */
    ariaLabel?: string;

    /** 
     * Placeholder in edition area
     * ! Will override the "placeholder" value defined in Quill config (configuration.placeholder)
     */
    placeholder?: ValueOrFunction<string>;

    /** 
     * Set callback to handle images in editor 
     * Images are by default set as blob in <img> and editor is saved in BDD as text
     * 
     * @returns new image src (image_url)
     */
    imagePicker?: {
        enable: boolean,
        imageUploadHandler: (image: File) => Promise<string>,

        /** 
         * minify the image
         *  - options.maxWidth {number} - specify the max width of the image, default is 800
         *  - options.maxHeight {number} - specify the max height of the image, default is 800
         *  - options.quality {number} - specify the quality of the image, default is 0.8 */
        minify?: IImageDataMinifyOption
    };

    /** 
     * Change the behavior of the keys to go to the new line
     * default - inserts a block (<p>) on Enter, and a <br> tag on Shift+Enter.
     * invert - swaps Enter and Shift+Enter behaviors.
     */
    newLineBehavior?: "default" | "invert";
}

export const defaultRichTextEditorOptions: RichTextEditorOptions = {
    configuration: "basic",
    disabled: false,
    multipleValuesState: false
};

export const QUILL_EDITOR_TOOLBAR_CLASS = "ql-toolbar";


/**
 * RichTextEditor use Quill library
 * @see https://quilljs.com/
 */
@CustomElement({
    name: "cjs-rich-text-editor",
    style: require("./richTextEditor.scss"),
    elementCssClasses: [FLEX_CONTAINER_COLUMN, H_100]
})
export class CjsRichTextEditor extends CjsEditor<string> {

    /** 
     * Triggered when input of editor has changed
     */
    @Event(NoBubblesEventInit)
    public inputEvent!: EventHandler<EditorOnChangeEvent<string>>;

    /** Attributes options of editor */
    @TypedWebComponentOptions(defaultRichTextEditorOptions)
    public readonly options!: WebComponentOptions<RichTextEditorOptions>;

    /** Textarea where Quill editor will be load */
    public quillEditorContainer?: HTMLElement;

    /** The RichTextEditor (from Quill library) */
    public quillEditor?: Quill;

    /** Get editor toolbar module */
    public get toolbar(): Toolbar | undefined {
        return this.quillEditor?.getModule("toolbar") as Toolbar | undefined;
    }

    /** Get editor history module */
    public get history(): History | undefined {
        return this.quillEditor?.getModule("history") as History | undefined;
    }

    /** Get editor clipboard module */
    public get clipboard(): Clipboard | undefined {
        return this.quillEditor?.getModule("clipboard") as Clipboard | undefined;
    }

    /** Access to html of toolbar */
    public get toolbarElt(): HTMLDivElement | undefined {
        return this.querySelector(`.${QUILL_EDITOR_TOOLBAR_CLASS}`) as HTMLDivElement | undefined;
    }

    /**
     * Configuration of RichTextEditor
     * Always re-build configuration from current options
     */
    public get configuration(): RichTextConfiguration {
        const configOption = this.options.configuration;

        let config: RichTextConfiguration;
        if (typeof configOption === "string") {// Base config key (basic/minimal/advanced/advancedToolbar)
            config = {
                ...richTextBaseConfig,
                modules: {
                    ...richTextBaseConfig.modules,
                    toolbar: BaseRichTextToolbarConfigurationsValues[configOption]// Use config key for toolbar
                }
            };
        } else {// Custom config defined by app
            config = configOption;
        }

        // Default behavior for bounds of editor
        if (!config.bounds) {
            // For popup set bounds to container of editor, not document body
            config.bounds = this.quillEditorContainer;
        }

        // Set placeholder if defined in options
        if (this.options.placeholder) {
            config.placeholder = getVOFValue(this.options.placeholder) ?? "";
        }

        // Install newLineInvert behavior
        if (this.options.newLineBehavior === "invert") {
            this._installNewlineInvert();
        }

        // If imagePicker is enable, add "imageDropAndPaste" module with image upload handler
        if (this.options.imagePicker?.enable) {
            config = {
                ...config,
                modules: {
                    ...config.modules,
                    imageDropAndPaste: {
                        handler: this.imageHandler.bind(this)
                    }
                }
            };
        }

        return config;
    }

    constructor(options?: Partial<RichTextEditorOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /**
     * When component is loaded
     */
    public override init(): void {
        super.init();

        // Create container of editor
        this.quillEditorContainer = createHtmlElement("div", {
            attributes: {
                id: "quillEditor_" + this.componentNumber,
                ariaLabel: this.options.ariaLabel ?? ""
            }
        });

        // Add container to editor
        this.append(this.quillEditorContainer);

    }

    /** @inheritdoc */
    public connectedCallback(): void {
        document.addEventListener("focusout", this._onEditorBlur);
    }

    /** @inheritdoc */
    public disconnectedCallback(): void {
        document.removeEventListener("focusout", this._onEditorBlur);
    }

    /** Refresh component */
    public override refresh(): void {
        // Refresh editor
        // ! Should be called before super.refresh, to instantiate quillEditor
        this._refreshQuillEditor();

        // Editor refresh
        super.refresh();

        // Refresh value displayed in edition area
        this._refreshInputValue();
    }

    /**
     * Refresh quill editor from current configuration
     * Destroy old editor and create a new one
     */
    protected _refreshQuillEditor(): void {
        // Can't refresh quill editor if container isn't defined
        if (!this.quillEditorContainer) { return; }

        // Delete previous quill editor
        if (this.quillEditor && this.quillEditorContainer) {
            this.toolbarElt?.remove();
            empty(this.quillEditorContainer);
            delete this.quillEditor;
        }

        // Init new quill Editor
        this.quillEditor = new Quill(this.quillEditorContainer, this.configuration);

        // Add custom module to edit links with modal
        const cjsLinkModule = new CjsQuillLinkModule(this.quillEditor);
        this.toolbar?.addHandler("link", cjsLinkModule.openCustomLinkModal.bind(cjsLinkModule));

        // If imagePicker is enable, install custom image upload for toolbar icon
        if (this.options.imagePicker?.enable) {
            this._initImageUploadListeners();
        }

        // Init listeners of editor
        this._initQuillListeners();
    }


    /** Init quill editor listeners */
    protected _initQuillListeners(): void {

        // Subscribe to quillEditor events
        this.quillEditor?.on("editor-change", (eventName, ...args) => {

            // Listen to content changes (triggered when editor or user change value in input)
            if (eventName === "text-change") {
                const source = args[2];// Can be "user"/"api"/"silent"

                // Only listen "user" changes ( "silent" <=> self editor changes)
                if (source === Quill.sources.USER) {
                    // Dispatch inputEvent, retrieve value currently edited in quillEditor (not RichTextEditor.value)
                    const currentValueInInput = this._getValueFromInputComponent() ?? "";

                    if (!this.disabled) {// Dispatch event only if editor isn't disabled
                        this.inputEvent.emit({ value: currentValueInInput });
                    }
                }
            }
        });
    }

    /**
     * @inheritdoc
     * Always retrieve value from current text displayed in input textarea
     */
    public override getValue(): string {
        return this._getValueFromInputComponent() ?? "";
    }

    /**
     * Do something to our dropped or pasted image
     * @param imageDataUrl {string} - image's dataURL
     * @param type {string} - image's mime type
     * @param imageData {ImageData} - provided more functions to handle the image
     *   - imageData.toBlob() {function} - convert image to a BLOB Object
     *   - imageData.toFile(filename?: string) {function} - convert image to a File Object. filename is optional, it will generate a random name if the original image didn't have a name.
     *   - imageData.minify(options) {function)- minify the image, return a promise
     *      - options.maxWidth {number} - specify the max width of the image, default is 800
     *      - options.maxHeight {number} - specify the max height of the image, default is 800
     *      - options.quality {number} - specify the quality of the image, default is 0.8
     */
    public async imageHandler(imageDataUrl: string, type: string, imageData: ImageData): Promise<void> {

        const quill = this.quillEditor;
        // If imagePicker is disabled or editor isn't build, don't perform image upload
        if (!this.options.imagePicker?.enable || !quill) { return; }

        let file: File | null = null;

        // Minify image added if requested
        if (this.options.imagePicker.minify) {
            await imageData.minify(this.options.imagePicker.minify).then((miniImageData) => {
                if (miniImageData instanceof ImageData) {
                    file = miniImageData.toFile();
                }
            });
        } else {// Classic image file, don't perform any modifications
            file = imageData.toFile();
        }
        if (!file) { return; }

        // Upload image to server
        this.options.imagePicker.imageUploadHandler(file).then((image_url: string) => {
            // success? you should return the uploaded image's url

            // then insert into the quill editor at current selection index
            let index = (quill.getSelection() || {}).index;
            if (index === undefined || index < 0) { index = quill.getLength(); }

            quill.insertEmbed(index, "image", image_url, Quill.sources.USER);// Source.USER will trigger a onInputValueChange

        }).catch((e) => {
            this._log?.error("Error in rich text during file upload", e);
        });
    }


    /** Install custom image upload for toolbar */
    protected _initImageUploadListeners(): void {
        const quill = this.quillEditor;
        if (!quill) { return; }

        // 1) Toolbar: replace image handler to upload and insert image URL
        this.toolbar?.addHandler("image", (clicked) => {
            if (!clicked) { return; }

            // Retrieve current input in toolbar or create a new one to open filePicker
            let fileInput = this.toolbarElt?.querySelector("input.ql-image[type=file]") as HTMLInputElement | undefined;
            if (fileInput == null) {
                fileInput = createHtmlElement("input", {
                    attributes: {
                        type: "file",
                        accept: "image/png, image/gif, image/jpeg, image/bmp, image/x-icon"
                    },
                    cssClass: ["ql-image"]
                });
            }

            // On file picked by user
            fileInput.addEventListener("change", (e) => {
                // Get the file picked
                if (!e.target) { return; }
                const files = asAnyObject(e.target)["files"];
                const file = files[0];
                if (!file) { return; }

                const type = file.type;
                const reader = new FileReader();
                reader.onload = (loadEvent) => {
                    // handle the inserted image
                    const dataUrl = loadEvent.target?.result;
                    if (typeof dataUrl === "string") {
                        // Upload image with handler
                        this.imageHandler(dataUrl, type, new ImageData(dataUrl, type, file.name));
                        fileInput.value = "";// Clear input
                    }
                };
                reader.readAsDataURL(file);
            });

            fileInput.click();
        });
    }


    /** Get quill editor html */
    public getInput(): HTMLElement {
        return this.quillEditor?.container ?? createHtmlElement("div");
    }

    /**
     * @returns validationFormElement
     * Where "is-invalid" class will be added / removed
     */
    public override getValidationFormElement(): HTMLElement[] {
        const toolbar = this.toolbarElt ?? createHtmlElement("div");
        const editionArea = this.getInput();
        return [toolbar, editionArea];
    }

    /** Get quill editor */
    public getQuillEditor(): Quill | undefined {
        return this.quillEditor;
    }

    /**
     * Redirect focus on input
     */
    public override focus(options?: FocusOptions): void {
        this.getQuillEditor()?.focus(options);
    }

    /**
     * Update the input value from stored value
     */
    protected _refreshInputValue(): void {
        if (!this.quillEditor) { return; }

        const htmlString = this._parseValueHTML(this._value ?? "");// * Handle spacing to ensure compatibility with old tinymceEditor

        // Convert HTMLstring to Quill Delta format
        const delta = this.quillEditor.clipboard.convert({ html: htmlString });
        this.quillEditor.setContents(delta, Quill.sources.SILENT);

        // Keep up-to-date value of editor (important to keep this line here)
        this._value = this._getValueFromInputComponent();
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): string | undefined {
        // Handle empty content
        if (this.isEmpty()) { return ""; }

        const valueHtml = this.quillEditor?.root.innerHTML ?? "";
        const valueHtmlParsed = this._parseValueHTML(valueHtml);
        return valueHtmlParsed;
    }

    /** Update disabled attribute on input component */
    protected _refreshDisabled(): void {
        if (!this.quillEditor) { return; }

        // Toggle disabled state
        this.quillEditor.enable(!this.disabled);

        // Toggle disabled class for RichTextEditor
        toggleClass(this, this.disabled, "disabled");
    }

    /** Parse htmlString to replace styling of nodes */
    private _parseValueHTML(htmlString: string): string {

        const parser = new DOMParser();
        const virtualDom = parser.parseFromString(htmlString, "text/html");
        const nodes = Array.from(virtualDom.body.children);

        let newHtmlString = "";
        if (!nodes.length && htmlString.length) {// Check for no nodes than can be parsed and some text passed => Simple text value, nothing to parse 
            newHtmlString = htmlString;
        } else {
            for (const node of nodes) {
                // Handle paragraph blocks
                if (node.tagName === "P" && node instanceof HTMLElement) {
                    // Remove bottom-margin;
                    node.style.marginBottom = toPx(0);

                    // If empty, set height style to simulate spacing
                    const isEmpty = node.innerHTML.trim() === "<br>" || node.innerHTML.trim() === "";
                    if (isEmpty) {
                        node.style.height = "1rem";
                    }
                }

                // Append node to returned html
                newHtmlString += node.outerHTML;
            }

        }
        return newHtmlString;
    }

    /** Editor blur listener callback */
    protected _onEditorBlur: EventListenerObject = {
        handleEvent: (e: FocusEvent) => {
            // Check if blur element is current editor
            if (e.target === this.quillEditor?.root && !this.disabled) {
                // If quillEditor isn't focused => user loose focus => dispatch value change
                this._onInputValueChange();
            }
        }
    };

    /**
     * Will install new line invert behavior for Quill Editor
     * @see RichTextEditorOptions.newLineBehavior
     */
    protected _installNewlineInvert(): void {
        const quillEditor = this.getQuillEditor();
        const keyboard = quillEditor?.keyboard;
        // Check if richTextEditor was init
        if (!keyboard) { return; }

        // SHIFT+ENTER => new block (paragraph)
        keyboard.addBinding({
            key: 13,// 13 <=> KeyboardInput.ENTER key
            shiftKey: true
        },
            (range: Range, context: anyReallyAny) => {
                if (!range) { return false; }
                const formats = context?.format; // Inherits the common format if present
                quillEditor.insertText(range.index, "\n", formats || Quill.sources.USER);
                quillEditor.setSelection(range.index + 1, Quill.sources.SILENT);
                return false; // Prevents default behavior (default: add <br>)
            });

        // ENTER => <br>
        keyboard.addBinding({ key: 13 }, (range: Range) => {
            if (!range) { return false; }
            quillEditor.insertEmbed(range.index, "break", true, Quill.sources.USER);
            quillEditor.setSelection(range.index + 1, Quill.sources.SILENT);
            return false; // Prevents default behavior (default: add <p>)
        });
    }

    /** 
     * Check is editor is empty, based on class of editor.
     * Can't check innerHtml of editor, QuillEditor should keep some nodes because cursor need it to work properly
     * @returns true if editor is empty
     */
    public isEmpty(): boolean {
        return Boolean(this.quillEditor?.root.classList.contains("ql-blank"));
    }
}